import { Injectable } from '@angular/core';
import { TranslateService, TranslationObject } from '@ngx-translate/core';
import { LanguageService } from '../services/language.service';

import de from '../translation/assets/i18n/de.json';
import en from '../translation/assets/i18n/en.json';
import es from '../translation/assets/i18n/es.json';
import fr from '../translation/assets/i18n/fr.json';

export type AppLang = 'en' | 'fr' | 'de' | 'es';

@Injectable({ providedIn: 'root' })
export class TranslationLoaderService {
  private shouldUseTranslations = false;

  private readonly defaultLang: AppLang = 'en';
  private translations: Record<AppLang, TranslationObject> = { en, fr, de, es };
  stored: AppLang | null = null;

  constructor(
    private translateService: TranslateService,
    private languageService: LanguageService
  ) {
  }

  setMultipleLanguages(flag: boolean) {
    this.shouldUseTranslations = flag;
  }

  // Validates the available languages based on the application type
  getAvailableLanguages(): AppLang[] {
    return this.shouldUseTranslations
      ? ['en', 'fr', 'de', 'es']
      : ['en'];
  }

  initialize(): void {
    const availableLanguages = this.getAvailableLanguages();

    this.translateService.addLangs(availableLanguages);
    this.translateService.setFallbackLang(this.defaultLang);

    this.loadAll(availableLanguages);

    // Fetches the preference from the endpoint and decides the language
    this.languageService.languages().subscribe(language => {
      const languageToUse = this.getStoredOrDefaultLang(language, availableLanguages);
      this.translateService.use(languageToUse);
    });
  }

  private getStoredOrDefaultLang(endpointLang: string | null, available: AppLang[]): AppLang {
    const stored = endpointLang as AppLang | null;

    if (stored && available.includes(stored)) {
      return stored;
    }

    return this.defaultLang;
  }

  private loadAll(available: AppLang[]): void {
    for (const lang of available) {
      const data = structuredClone(this.translations[lang]);
      this.validateTranslation(data, lang);
      this.translateService.setTranslation(lang, data, true);
    }
  }

  private validateTranslation(data: object, lang: AppLang) {
    const emptyKeys: string[] = [];

    const check = (obj: object, path: string[] = []) => {
      for (const [key, value] of Object.entries(obj)) {
        const currentPath = [...path, key];

        if (typeof value === 'object' && value !== null) {
          check(value, currentPath);
        }
        else if (typeof value === 'string' && value.trim() === '') {
          const fullKey = currentPath.join('.');
          emptyKeys.push(fullKey);
          obj[key] = fullKey;
        }
      }
    };

    check(data);

    if (emptyKeys.length) {
      console.warn(`[i18n] Empty translations in '${lang}':`, emptyKeys);
    }
  }
}
